import React, {useId} from 'react'
import {useTranslation} from 'react-i18next'
import {ActivationPolicy} from '../../domain/entities/Activation.ts'

// A native checkbox (keyboard + screen reader for free) with the design's
// rounded box. Animations limited to the colour change.
export const ActivationCheckbox: React.FC<{
    checked: boolean
    onChange: (checked: boolean) => void
    disabled?: boolean
    children: React.ReactNode
    describedBy?: string
}> = ({checked, onChange, disabled, children, describedBy}) => {
    const id = useId()
    return (
        <label htmlFor={id} className={`flex items-start gap-2.5 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <input
                id={id}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                aria-describedby={describedBy}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-none rounded-[5px] border-[1.5px] border-neutralBlack-400 accent-neutralBlack-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutralBlack-900"
            />
            <span className="text-[12.5px] leading-[1.5] text-neutralBlack-700">{children}</span>
        </label>
    )
}

interface ActivationContactFieldsProps {
    policy: ActivationPolicy
    noContact: boolean
    onNoContactChange: (noContact: boolean) => void
    consentGranted: boolean
    onConsentChange: (granted: boolean) => void
    // Shown under the fields with role="alert" (backend or validation error).
    error?: string | null
}

// The activation part of every traveler-contact form: which contact the market
// makes mandatory, the explicit "No lo tengo" (route B) and the versioned
// consent checkbox. The contact inputs themselves stay in the caller's layout.
export const ActivationContactFields: React.FC<ActivationContactFieldsProps> = ({
    policy, noContact, onNoContactChange, consentGranted, onConsentChange, error,
}) => {
    const {t} = useTranslation('dashboard')
    const noContactHelpId = useId()
    const requiredLabel = policy.requiredContact === 'PHONE' ? t('activation.contact.phone') : t('activation.contact.email')

    return (
        <div className="flex flex-col gap-3">
            {!noContact && (
                <p className="text-xs leading-[1.5] text-neutralBlack-600">
                    {t('activation.contact.requiredHint', {contact: requiredLabel})}
                </p>
            )}
            <ActivationCheckbox checked={noContact} onChange={onNoContactChange} describedBy={noContactHelpId}>
                <span className="font-semibold text-neutralBlack-900">{t('activation.contact.noContact')}</span>
                <span id={noContactHelpId} className="block text-neutralBlack-600">{t('activation.contact.noContactHelp')}</span>
            </ActivationCheckbox>
            <ActivationCheckbox checked={consentGranted && !noContact} onChange={onConsentChange} disabled={noContact}>
                {t('activation.consent.text')}
            </ActivationCheckbox>
            {!noContact && !consentGranted && (
                <p className="text-xs leading-[1.5] text-orange-700">{t('activation.consent.missingWarning')}</p>
            )}
            {error && (
                <p role="alert" className="text-[12.5px] leading-[1.5] font-medium text-[#B3261E]">{error}</p>
            )}
        </div>
    )
}
